import { gql, useQuery } from '@apollo/client';
import { type CalendarDate } from '@internationalized/date';
import Avatar from '@orn/shared-web/components/Avatar';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { getInitial } from '@orn/shared-web/utils/person/getInitials';
import * as React from 'react';

import { PaginationDirection, TransactionSortKeys, type TransactionFilters } from '../../__generated__/globalTypes';
import { PATHS } from '../../paths';
import { useNavigateWithDateRange } from '../../providers/DateRangeProvider';
import { formatCalendarDate, toCurrency } from '../../utils/formatting';
import { useDateRangeDisplayText, type CalendarDateRange } from '../DateRangePicker';
import Entity from '../entity';
import Widget from '../Widget';

import type {
  GetLargestTransactionsQuery,
  GetLargestTransactionsQueryVariables,
} from './__generated__/LargestTransactions.gql';

const PAGE_SIZE = 5;

// Minimal fragment for largest transactions widget display
const LargestTransactionFragment = gql`
  fragment LargestTransaction on Transaction {
    id
    amount
    date
    description
    merchant {
      id
      name
      logoUrl
      isMantle
    }
  }
`;

const GET_LARGEST_TRANSACTIONS = gql`
  query GetLargestTransactions(
    $pagination: PaginationInput!
    $filters: TransactionFilters
    $sortKey: TransactionSortKeys
  ) {
    getTransactionsForUser(pagination: $pagination, filters: $filters, sortKey: $sortKey) {
      edges {
        node {
          ...LargestTransaction
        }
      }
    }
  }
  ${LargestTransactionFragment}
`;

type Transaction = NonNullable<
  NonNullable<GetLargestTransactionsQuery['getTransactionsForUser']>['edges']
>[number]['node'];

interface IBaseLargestTransactionsProps {
  transactions: Transaction[];
  dateRange: CalendarDateRange;
  isLoading?: boolean;
}

export const BaseLargestTransactions = ({ transactions, dateRange, isLoading }: IBaseLargestTransactionsProps) => {
  const navigate = useNavigateWithDateRange();

  const dateRangeDisplayText = useDateRangeDisplayText(dateRange, 'past6Months');

  return (
    <>
      <Widget>
        <Widget.Header
          title="Largest transactions"
          trailing={<span className="text-tertiary text-xs font-medium">{dateRangeDisplayText}</span>}
        />

        <Widget.Body className="pb-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingIndicator size="md" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-tertiary px-5 py-8 text-center text-sm">No transactions found</div>
          ) : (
            transactions.map((transaction) => {
              const merchantIcon = transaction.merchant ? (
                <Entity.Avatar entity={transaction.merchant} size="sm" />
              ) : (
                <Avatar contrastBorder={true} size="sm" initials={getInitial(transaction.description)} />
              );

              const label = (
                <div className="flex min-w-0 flex-1 flex-col items-start overflow-hidden">
                  <span className="text-primary w-full truncate text-base font-medium">
                    {transaction.merchant?.name ?? transaction.description}
                  </span>
                  <span className="text-tertiary w-full truncate text-sm">{formatCalendarDate(transaction.date)}</span>
                </div>
              );

              return (
                <Widget.Row
                  key={transaction.id}
                  className="hover:bg-utility-brand-25 cursor-pointer py-4"
                  label={label}
                  value={toCurrency(Number(transaction.amount))}
                  icon={merchantIcon}
                  onClick={() => {
                    void navigate(PATHS.TRANSACTION(transaction.id));
                  }}
                />
              );
            })
          )}
        </Widget.Body>
      </Widget>
    </>
  );
};

interface ILargestTransactionsProps {
  dateRange: CalendarDateRange;
}

const LargestTransactions = (props: ILargestTransactionsProps) => {
  const { dateRange } = props;

  const filters = React.useMemo<TransactionFilters>(() => {
    const filter: TransactionFilters = {
      excludePaired: true,
    };
    if (dateRange) {
      filter.startDate = dateRange.start.toString() as unknown as CalendarDate;
      filter.endDate = dateRange.end.toString() as unknown as CalendarDate;
    }
    return filter;
  }, [dateRange]);

  const dateRangeDisplayText = useDateRangeDisplayText(dateRange);

  const { data, loading, error } = useQuery<GetLargestTransactionsQuery, GetLargestTransactionsQueryVariables>(
    GET_LARGEST_TRANSACTIONS,
    {
      variables: {
        pagination: {
          cursor: undefined,
          limit: PAGE_SIZE,
          direction: PaginationDirection.Forward,
        },
        filters,
        sortKey: TransactionSortKeys.Amount,
      },
    }
  );

  if (error) {
    return (
      <Widget>
        <Widget.Header title="Largest transactions" subtitle={dateRangeDisplayText} />
        <Widget.Body>
          <div className="text-error px-5 py-8 text-center text-sm">Error loading transactions: {error.message}</div>
        </Widget.Body>
      </Widget>
    );
  }

  const transactions = data?.getTransactionsForUser?.edges.map((edge) => edge.node) ?? [];

  return <BaseLargestTransactions transactions={transactions} dateRange={dateRange} isLoading={loading} />;
};

export default LargestTransactions;
